import { QueryClient } from "@tanstack/react-query";

import { BidOrchestratorStatusEvent_StreamingQuestions } from "../../generated/app-service-gql/graphql";
import {
  BidNoBidResponse,
  updateBidReportAnswersCache,
} from "../../hooks/api/bid/answers/useGetBidReportAnswers";
import { BidOrchestratorPhase, BidOrchestratorSection } from "../../types/graphQLEnums";
import { BidOrchestratorEntry } from "./types";

/**
 * Seeds per-section state on the `*_report_started` event:
 *  - the canonical question metadata (bidQuestionId + groupName) used by
 *  - chunk handlers to find the right (group, question) in the answers cache
 *  - the answers cache, primed with the full question list so the UI can
 *  - render question titles up-front and fill answers in as chunks stream in
 */
export function initialiseBidReportQuestions({
  newEntry,
  streamingQuestions,
  phase,
  queryClient,
}: {
  newEntry: BidOrchestratorEntry;
  streamingQuestions: BidOrchestratorStatusEvent_StreamingQuestions[];
  phase: BidOrchestratorPhase;
  queryClient: QueryClient;
}): BidOrchestratorEntry {
  const initialReportType: keyof BidOrchestratorEntry["bidReportRelease"] =
    phase === BidOrchestratorPhase.RequirementSummaryReportStarted
      ? BidOrchestratorSection.RequirementsSummary
      : BidOrchestratorSection.QualificationReport;
  const bidReportId =
    initialReportType === BidOrchestratorSection.RequirementsSummary
      ? newEntry.requirementSummaryReportId
      : newEntry.qualificationReportId;

  const sortedQuestions = streamingQuestions.map((q) => ({
    bidQuestionId: q.bidQuestionId,
    groupName: q.groupName,
  }));

  if (bidReportId) {
    const initialBidReportQuestions = serialiseStreamedQuestions(streamingQuestions);

    updateBidReportAnswersCache(queryClient, bidReportId, (prev) => ({
      ...prev,
      result: initialBidReportQuestions,
    }));
  }

  return {
    ...newEntry,
    bidReportRelease: {
      ...newEntry.bidReportRelease,
      [initialReportType]: {
        sortedQuestions,
        nextReleaseIndex: 0,
        pendingAnswers: {},
      },
    },
  };
}

/**
 * Build the persisted `getBidReportAnswers.result` shape (groupName + ordered
 * questions with empty answers) from the canonical streaming questions list.
 */
function serialiseStreamedQuestions(
  questions: BidOrchestratorStatusEvent_StreamingQuestions[],
): BidNoBidResponse {
  const groupOrder: string[] = [];
  const grouped: Record<string, BidNoBidResponse[number]["questions"]> = {};
  for (const q of questions) {
    if (!grouped[q.groupName]) {
      grouped[q.groupName] = [];
      groupOrder.push(q.groupName);
    }
    grouped[q.groupName].push({
      // bidQuestionId doubles as the cache lookup key since real answerIds
      // don't exist until the answers are persisted post-stream.
      answerId: q.bidQuestionId,
      autoQualify: true,
      title: q.title,
      order: q.order,
      answer: { answer: "" },
      sources: [],
    });
  }

  return groupOrder.map((name) => ({ groupName: name, questions: grouped[name] }));
}
